// ClaudeCode: Main vault page — list, search, navigate to detail/create
import React, { useState, useEffect, useCallback } from 'react';
import { listEntries, searchEntries, updateEntry } from '../api';
import type { EntryMeta } from '../api';
import { EntryCard } from '../components/EntryCard';
import { EntryDetail } from '../components/EntryDetail';
import { CreateEntry } from '../components/CreateEntry';

interface VaultHomeProps {
  cryptoKey: CryptoKey;
  onLock: () => void;
  displayName: string;
}

type View = 'list' | 'detail' | 'create';

export function VaultHome({ cryptoKey, onLock, displayName }: VaultHomeProps) {
  const [entries, setEntries] = useState<EntryMeta[]>([]);
  const [view, setView] = useState<View>('list');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState<number | null>(null);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      if (searchQuery.trim()) {
        const res = await searchEntries(searchQuery.trim());
        setEntries(res.entries);
      } else {
        const res = await listEntries({
          limit: 100,
          tier: filterTier ?? undefined,
        });
        setEntries(res.entries);
      }
    } catch {
      // silently handle — entries will be empty
    } finally {
      setLoading(false);
    }
  }, [searchQuery, filterTier]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleToggleFavorite = async (id: string, current: boolean) => {
    await updateEntry(id, { favorite: !current });
    loadEntries();
  };

  // Entry detail view
  if (view === 'detail' && selectedId) {
    return (
      <EntryDetail
        entryId={selectedId}
        cryptoKey={cryptoKey}
        onBack={() => {
          setView('list');
          setSelectedId(null);
        }}
        onDeleted={() => {
          setView('list');
          setSelectedId(null);
          loadEntries();
        }}
      />
    );
  }

  // Create entry view
  if (view === 'create') {
    return (
      <CreateEntry
        cryptoKey={cryptoKey}
        onCreated={() => {
          setView('list');
          loadEntries();
        }}
        onCancel={() => setView('list')}
      />
    );
  }

  // List view
  return (
    <div style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: '#fafafa',
              margin: 0,
            }}
          >
            Vault
          </h1>
          <span style={{ fontSize: 13, color: '#71717a' }}>{displayName}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onLock} style={headerBtnStyle} title="Lock vault">
            {'\uD83D\uDD12'}
          </button>
        </div>
      </div>

      {/* Search and filter bar */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
        }}
      >
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search titles, tags, URLs..."
          style={{
            flex: 1,
            padding: '10px 12px',
            background: '#09090b',
            border: '1px solid #27272a',
            borderRadius: 6,
            color: '#fafafa',
            fontSize: 14,
            outline: 'none',
          }}
        />
        <select
          value={filterTier ?? ''}
          onChange={(e) =>
            setFilterTier(e.target.value ? parseInt(e.target.value, 10) : null)
          }
          style={{
            padding: '10px 12px',
            background: '#09090b',
            border: '1px solid #27272a',
            borderRadius: 6,
            color: '#a1a1aa',
            fontSize: 13,
            outline: 'none',
            cursor: 'pointer',
          }}
        >
          <option value="">All tiers</option>
          <option value="4">T4 Trivial</option>
          <option value="3">T3 Convenience</option>
          <option value="2">T2 Sensitive</option>
          <option value="1">T1 Irreplaceable</option>
        </select>
        <button
          onClick={() => setView('create')}
          style={{
            padding: '10px 16px',
            background: '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + New
        </button>
      </div>

      {/* Entry list */}
      {loading ? (
        <div style={{ color: '#71717a', textAlign: 'center', padding: 40 }}>
          Loading...
        </div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#52525b' }}>
          {searchQuery
            ? 'No entries match your search'
            : 'Your vault is empty. Add your first secret.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              onClick={(id) => {
                setSelectedId(id);
                setView('detail');
              }}
              onToggleFavorite={handleToggleFavorite}
            />
          ))}
        </div>
      )}

      {/* Footer stats */}
      {entries.length > 0 && (
        <div
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: '#52525b',
            marginTop: 24,
          }}
        >
          {entries.length} entries
        </div>
      )}
    </div>
  );
}

const headerBtnStyle: React.CSSProperties = {
  padding: '6px 12px',
  background: '#27272a',
  border: '1px solid #3f3f46',
  borderRadius: 6,
  fontSize: 16,
  cursor: 'pointer',
  lineHeight: 1,
};
